import { useState } from 'react'
import {
  AlertTriangle, Banknote, ChevronDown, ChevronUp, History, Inbox, Mail, PauseCircle, Receipt, Trash2, UserCheck,
  UserMinus, UserPlus, type LucideIcon,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatDate, formatDateOrDash } from '@/lib/utils'
import { etiquetaEstadoEmail, etiquetaTipoEmail } from '@/lib/email-labels'
import {
  TITULO_EVENTO_CLIENTE,
  etiquetaCanal,
  etiquetaEtapaAviso,
  type EventoLinea,
  type EventoMail,
} from '@/lib/lineaDeTiempoCliente'
import { useLineaDeTiempoCliente } from '@/hooks/useLineaDeTiempoCliente'
import { useEmailLog } from '@/hooks/useEmailLog'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'

const PASO = 25

const ICONO: Record<EventoLinea['tipo'], LucideIcon> = {
  deuda: Receipt,
  pago: Banknote,
  eliminado: Trash2,
  mail: Mail,
  alta: UserPlus,
  baja: UserMinus,
  reactivacion: UserCheck,
  suspension: PauseCircle,
}

const FALLA: Record<'movimientos' | 'mails' | 'auditoria', string> = {
  movimientos: 'No se pudieron cargar las deudas y los pagos.',
  mails: 'No se pudieron cargar los mails.',
  auditoria: 'No se pudieron cargar las altas, las bajas ni los movimientos borrados.',
}

const TRUNCADO: Record<'movimientos' | 'mails' | 'auditoria', string> = {
  movimientos: 'Se muestran solo los movimientos más recientes.',
  mails: 'Se muestran solo los mails más recientes.',
  auditoria: 'Se muestran solo los registros de auditoría más recientes.',
}

const fechaHora = (timestamp: string) =>
  new Date(timestamp).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })

function ContenidoMail({ mailId }: { mailId: string }) {
  const { organizacionId } = useOrganizacion()
  const { data, isLoading, isError } = useEmailLog(mailId, organizacionId ?? undefined)

  if (isLoading) return <div className="h-24 bg-muted animate-pulse rounded-md" />
  if (isError) return <p className="text-sm text-destructive">No se pudo cargar el contenido del mail.</p>
  if (!data?.body_html) {
    return <p className="text-sm text-muted-foreground">El contenido de este mail no fue registrado.</p>
  }
  return (
    <iframe
      srcDoc={data.body_html}
      sandbox=""
      referrerPolicy="no-referrer"
      title="Contenido del mail"
      className="w-full h-[400px] rounded-md border bg-white"
    />
  )
}

function DetalleMail({ evento, verContenido }: { evento: EventoMail; verContenido: boolean }) {
  const [abierto, setAbierto] = useState(false)
  const { mail } = evento
  const estado = etiquetaEstadoEmail(mail.status)
  const etapa = typeof mail.metadata?.etapa === 'string' ? mail.metadata.etapa : null
  const canal = typeof mail.metadata?.canal === 'string' ? mail.metadata.canal : null
  const esManual = mail.status === 'manual'
  const adjuntos = mail.attachments_meta ?? []

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium">{etiquetaTipoEmail(mail.tipo)}</span>
        {etapa && <Badge variant="secondary" className="text-xs">{etiquetaEtapaAviso(etapa)}</Badge>}
        <Badge variant={estado.variant} className="text-xs">{estado.label}</Badge>
      </div>
      <p className="text-xs text-muted-foreground truncate">
        {esManual ? `Registrado por ${mail.from}${canal ? ` · ${etiquetaCanal(canal)}` : ''}` : mail.subject}
      </p>
      {adjuntos.length > 0 && (
        <p className="text-xs text-muted-foreground truncate">Adjuntos: {adjuntos.map((a) => a.filename).join(', ')}</p>
      )}
      {verContenido && !esManual && (
        <Button variant="link" className="p-0 h-auto text-xs" onClick={() => setAbierto((v) => !v)}>
          {abierto ? 'Ocultar contenido' : 'Ver contenido'}
          {abierto ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
        </Button>
      )}
      {abierto && <ContenidoMail mailId={mail.id} />}
    </>
  )
}

function DetalleEvento({ evento, verContenido }: { evento: EventoLinea; verContenido: boolean }) {
  switch (evento.tipo) {
    case 'deuda':
    case 'pago':
      return (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{evento.tipo === 'pago' ? 'Pago registrado' : 'Deuda generada'}</span>
            <span className={`text-sm font-semibold tabular-nums ${evento.tipo === 'pago' ? 'text-green-600' : ''}`}>
              {formatCurrency(evento.monto)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {evento.concepto}
            {evento.periodo ? ` · período ${evento.periodo}` : ''}
          </p>
          {(evento.cargadoEl || evento.cargadoPor) && (
            <p className="text-xs text-muted-foreground">
              Cargado{evento.cargadoEl ? ` el ${fechaHora(evento.cargadoEl)}` : ''}
              {evento.cargadoPor ? ` por ${evento.cargadoPor}` : ''}
            </p>
          )}
        </>
      )
    case 'eliminado':
      return (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-amber-700 dark:text-amber-500">
              {evento.tipoMovimiento} {evento.tipoMovimiento === 'Deuda' ? 'eliminada' : 'eliminado'}
            </span>
            <span className="text-sm font-semibold tabular-nums line-through">{formatCurrency(evento.monto)}</span>
          </div>
          <p className="text-xs text-muted-foreground truncate">{evento.concepto}</p>
          <p className="text-xs text-muted-foreground">
            Lo borró {evento.borradoPor}
            {evento.fechaMovimiento
              ? `. Era del ${formatDate(evento.fechaMovimiento)}`
              : evento.cargadoEl
                ? `. Se había cargado el ${formatDate(evento.cargadoEl)}`
                : ''}
          </p>
        </>
      )
    case 'mail':
      return <DetalleMail evento={evento} verContenido={verContenido} />
    default:
      return (
        <>
          <span className="text-sm font-medium">{TITULO_EVENTO_CLIENTE[evento.tipo]}</span>
          <p className="text-xs text-muted-foreground">por {evento.usuario}</p>
        </>
      )
  }
}

/** Lista cronológica de deudas, pagos, borrados, mails y cambios de estado del cliente, la más nueva primero. */
export function LineaDeTiempoCliente({ clienteId }: { clienteId: string }) {
  const { eventos, verMovimientos, cargando, fallas, truncado } = useLineaDeTiempoCliente(clienteId)
  const [visibles, setVisibles] = useState(PASO)
  const fuentesCaidas = (Object.keys(fallas) as Array<keyof typeof fallas>).filter((f) => fallas[f])
  const fuentesCortadas = (Object.keys(truncado) as Array<keyof typeof truncado>).filter((f) => truncado[f])

  return (
    <div>
      <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
        <History className="h-4 w-4" />
        Línea de tiempo
      </h3>

      {!verMovimientos && (
        <p className="text-xs text-muted-foreground mb-3">
          Tu rol no ve deudas ni pagos: se muestran los mails y los cambios de estado del cliente.
        </p>
      )}
      {[...fuentesCaidas.map((f) => FALLA[f]), ...fuentesCortadas.map((f) => TRUNCADO[f])].map((aviso) => (
        <p key={aviso} className="flex items-center gap-2 text-xs text-destructive mb-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {aviso}
        </p>
      ))}

      {cargando ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : eventos.length === 0 ? (
        fuentesCaidas.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Inbox className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin actividad registrada.</p>
          </div>
        )
      ) : (
        <ol className="space-y-2">
          {eventos.slice(0, visibles).map((evento) => {
            const Icono = ICONO[evento.tipo]
            return (
              <li key={evento.id} className="flex gap-3 rounded-lg border p-3">
                <Icono className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <DetalleEvento evento={evento} verContenido={verMovimientos} />
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{formatDateOrDash(evento.fecha)}</span>
              </li>
            )
          })}
        </ol>
      )}

      {!cargando && eventos.length > visibles && (
        <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setVisibles((v) => v + PASO)}>
          Mostrar más ({eventos.length - visibles} restantes)
        </Button>
      )}
    </div>
  )
}
